import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";

import { createIntermediateInvoice } from "api";
import Button from "components/atoms/Button/Button";
import Dialog from "components/organisms/Dialog/Dialog";
import errorToast from "helpers/errorToast";

interface IntermediateInvoiceDialogProps {
  isOpen: boolean;
  close: () => void;
}

export default function IntermediateInvoiceDialog({ isOpen, close }: IntermediateInvoiceDialogProps) {
  const intl = useIntl();
  const navigate = useNavigate();
  const { id } = useParams();

  const { mutate: createIntermediateInvoiceMutation, isLoading } = useMutation({
    mutationFn: () => createIntermediateInvoice(id),
    onSuccess: () => {
      navigate(`/shipment/${id}/billing`);
      close();
    },
    onError: () =>
      errorToast(
        intl.formatMessage({
          id: "components.molecules.top-nav.intermediateInvoiceDialog.failSubmit",
          defaultMessage: "Fail to create intermediate invoice.",
        }),
      ),
  });

  const handleSubmitIntermediateInvoice = () => {
    createIntermediateInvoiceMutation();
  };

  return (
    <Dialog
      show={isOpen}
      title={intl.formatMessage({
        id: "components.molecules.top-nav.intermediateInvoiceDialog.title",
        defaultMessage: "Intermediate invoice",
      })}
      onHide={close}
      className="template-dialog-wrapper"
    >
      <h5 className="mb-3">
        <FormattedMessage
          id="components.molecules.top-nav.intermediateInvoiceDialog.message1"
          defaultMessage="Are you sure you want to submit an intermediate invoice?"
        />
      </h5>

      <p className="mb-3">
        <FormattedMessage
          id="components.molecules.top-nav.intermediateInvoiceDialog.message2"
          defaultMessage="The intermediate invoice is not a replacement of your final invoice."
        />
      </p>

      <p className="mb-3">
        <FormattedMessage
          id="components.molecules.top-nav.intermediateInvoiceDialog.message3"
          defaultMessage="Please ensure that shipper is aware of the incoming intermediate invoice."
        />
      </p>

      <hr className="mt-4 mb-4" />

      <div className="d-flex justify-content-between">
        <Button variant="danger" onClick={close} disabled={isLoading}>
          <FormattedMessage id="general.cancel" defaultMessage="Cancel" />
        </Button>
        <Button variant="primary" onClick={handleSubmitIntermediateInvoice} disabled={isLoading}>
          <FormattedMessage id="general.submit" defaultMessage="Submit" />
        </Button>
      </div>
    </Dialog>
  );
}
